import { DislikeOutlined, LikeOutlined } from "@ant-design/icons";
import { App } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { sendAssistantFeedback, type AssistantVote } from "../../../api/assistant";
import { useViewport } from "../../../hooks/useViewport";

// 👍 записва веднага; 👎 отваря поле за коментар („какъв е верният отговор")
// и записва с „Изпрати" — коментарът е по избор. Оценката може да се смени:
// всеки запис презаписва същия ред в `assistant_exchanges`.
export const AssistantFeedback = ({ exchangeId }: { exchangeId: number | null }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isNarrow } = useViewport();
  const [vote, setVote] = useState<AssistantVote | null>(null);
  const [saved, setSaved] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  if (exchangeId === null) {
    return <div className="text-[11.5px] text-muted">{t("monitoring.assistantNotRecorded")}</div>;
  }

  const submit = async (nextVote: AssistantVote, nextComment: string) => {
    setSending(true);
    try {
      await sendAssistantFeedback(exchangeId, nextVote, nextComment);
      setSaved(true);
      setCommentOpen(false);
    } catch {
      message.error(t("monitoring.assistantFeedbackError"));
    } finally {
      setSending(false);
    }
  };

  const choose = (nextVote: AssistantVote) => {
    setVote(nextVote);
    setSaved(false);
    if (nextVote === "up") {
      void submit("up", "");
    } else {
      setCommentOpen(true);
    }
  };

  const voteButton = (value: AssistantVote) => {
    const Icon = value === "up" ? LikeOutlined : DislikeOutlined;
    const active = vote === value;
    return (
      <button
        type="button"
        disabled={sending}
        onClick={() => choose(value)}
        aria-pressed={active}
        aria-label={t(value === "up" ? "monitoring.assistantVoteUp" : "monitoring.assistantVoteDown")}
        title={t(value === "up" ? "monitoring.assistantVoteUp" : "monitoring.assistantVoteDown")}
        className={`flex h-[28px] w-[32px] cursor-pointer items-center justify-center rounded-[8px] border transition-colors duration-150 disabled:cursor-default ${
          active
            ? "border-brand-ink bg-brand-tint text-brand-deep-ink"
            : "border-shade-2 bg-transparent text-muted hover:bg-shade-1 hover:text-ink"
        }`}
      >
        <Icon className="!text-[14px]" />
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex flex-wrap items-center gap-[8px] text-[12px] text-muted">
        <span>{t("monitoring.assistantHelpful")}</span>
        {voteButton("up")}
        {voteButton("down")}
        {saved && <span className="text-ok-deep">{t("monitoring.assistantFeedbackSaved")}</span>}
      </div>
      {commentOpen && (
        <div className={`flex gap-[8px] ${isNarrow ? "flex-col" : "flex-row items-end"}`}>
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            maxLength={2000}
            rows={2}
            placeholder={t("monitoring.assistantCommentPlaceholder")}
            // 16px на телефон: под 16px iOS Safari зуумва при фокус
            className={`min-h-[56px] flex-1 resize-y rounded-[10px] border border-shade-3 bg-surface px-[10px] py-[8px] font-[inherit] leading-[1.45] text-ink outline-none focus:border-brand-ink ${isNarrow ? "text-[16px]" : "text-[13px]"}`}
          />
          <button
            type="button"
            disabled={sending}
            onClick={() => void submit("down", comment)}
            className="h-[34px] cursor-pointer rounded-[8px] border-none bg-brand px-[14px] text-[12.5px] font-semibold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-default disabled:opacity-60"
          >
            {t("monitoring.assistantCommentSend")}
          </button>
        </div>
      )}
    </div>
  );
};
